import { useState } from "react";

import { IAdminPatientDetail } from "../../../../@types/admin/AdminPatient";
import { useChangePatientWards } from "../../../../hooks/mutations/admin/patients";
import {
  useHospitalOptions,
  useWardOptions,
} from "../../../../hooks/queries/admin/selectValues";
import { useToast } from "../../../../hooks/useToast";
import { errorToastMessage } from "../../../../utils/HttpError";
import {
  CheckboxField,
  FieldHint,
  FilterButton,
  FilterGroup,
  FilterLabel,
  ModalBodyText,
  ModalContainer,
  ModalForm,
  ModalFormActions,
  ModalTitle,
  WarningNotice,
} from "../AdminPage.styles";
import { AdminSelectField } from "../AdminSelectField";

interface IChangeWardModalProps {
  patient: IAdminPatientDetail;
  onDone: () => void;
}

/**
 * Moves a patient between wards (EN-5170) — Retool's
 * `moduleChangePatientWard_MultipleWards`.
 *
 * ⚠️ One of the two most consequential writes on this page. The ward set
 * decides which clinicians can see the patient, which measurement cards their
 * app shows, and — since EN-4960 — which workflows and alerts evaluate for
 * them, from ALL their wards rather than just the primary. Retool asked with a
 * bare "Are you sure you want to change the ward?"; this names what is
 * changing and what it will do.
 *
 * **Only the multi-ward module is ported**, per the ticket: Retool also has a
 * single-ward `moduleChangePatientWard` used by Archived Patients, and this
 * component serves both pages.
 *
 * `wardIds` is the whole new set, not a delta, and the FIRST entry becomes the
 * primary ward. So the primary is chosen explicitly here rather than left to
 * whatever order the checkboxes happened to produce — Retool sent
 * `multiselectWards.selectedItems.map(w => w.id)`, whose order was the
 * selection order, which means its primary ward was decided by the sequence of
 * clicks.
 */
export function ChangeWardModal({ patient, onDone }: IChangeWardModalProps) {
  const { showToast } = useToast();
  const { hospitals } = useHospitalOptions();
  const { changeWards, isChangingWards } = useChangePatientWards();

  const currentWardIds = (patient.wards ?? []).map((ward) => ward.wardId);
  const currentPrimary =
    (patient.wards ?? []).find((ward) => ward.isPrimary)?.wardId ??
    patient.wardId ??
    null;

  const [hospitalId, setHospitalId] = useState<string>(
    patient.hospitalId ? String(patient.hospitalId) : ""
  );
  const [selectedWardIds, setSelectedWardIds] =
    useState<number[]>(currentWardIds);
  const [primaryWardId, setPrimaryWardId] = useState<number | null>(
    currentPrimary
  );

  const { wards, isLoading: wardsLoading } = useWardOptions(
    hospitalId ? Number(hospitalId) : undefined
  );

  const handleHospitalChange = (value: string) => {
    setHospitalId(value);
    // The ward list is scoped to the hospital group, so a selection made
    // against the previous group is meaningless here. Retool left the
    // multiselect populated with the old group's ids, which could submit a
    // ward the patient's new hospital does not contain.
    setSelectedWardIds([]);
    setPrimaryWardId(null);
  };

  const toggleWard = (wardId: number) => {
    setSelectedWardIds((previous) => {
      const next = previous.includes(wardId)
        ? previous.filter((id) => id !== wardId)
        : [...previous, wardId];

      // Un-ticking the primary hands the role to whatever is left, so the
      // request can never name a primary that is not in the set.
      if (!next.includes(wardId) && primaryWardId === wardId) {
        setPrimaryWardId(next[0] ?? null);
      }
      if (next.length === 1) {
        setPrimaryWardId(next[0]);
      }
      return next;
    });
  };

  const effectivePrimary =
    primaryWardId !== null && selectedWardIds.includes(primaryWardId)
      ? primaryWardId
      : selectedWardIds[0] ?? null;

  const unchanged =
    selectedWardIds.length === currentWardIds.length &&
    selectedWardIds.every((id) => currentWardIds.includes(id)) &&
    effectivePrimary === currentPrimary;

  const canSave =
    selectedWardIds.length > 0 && effectivePrimary !== null && !unchanged;

  const handleSave = async () => {
    if (!canSave || effectivePrimary === null) return;

    // Primary first — the API takes the head of the list as the primary ward.
    const wardIds = [
      effectivePrimary,
      ...selectedWardIds.filter((id) => id !== effectivePrimary),
    ];

    try {
      await changeWards({ patientId: Number(patient.id), wardIds });
      showToast("The patient was successfully moved.", "success");
      onDone();
    } catch (error) {
      showToast(
        errorToastMessage(error, "There was an error moving this patient."),
        "warning"
      );
    }
  };

  const losingWards = (patient.wards ?? []).filter(
    (ward) => !selectedWardIds.includes(ward.wardId)
  );

  return (
    <ModalContainer>
      <ModalTitle>Change wards</ModalTitle>
      <ModalForm>
        <ModalBodyText>
          Current wards:{" "}
          <strong>
            {(patient.wards ?? []).length > 0
              ? (patient.wards ?? [])
                  .map(
                    (ward) =>
                      `${ward.wardName}${ward.isPrimary ? " (primary)" : ""}`
                  )
                  .join(", ")
              : "none"}
          </strong>
        </ModalBodyText>

        <FilterGroup>
          <FilterLabel htmlFor="change-ward-hospital">Hospital</FilterLabel>
          <AdminSelectField
            id="change-ward-hospital"
            value={hospitalId}
            onChange={(e) => handleHospitalChange(e.target.value)}
          >
            <option value="">Select a hospital</option>
            {hospitals.map((hospital) => (
              <option key={hospital.id} value={String(hospital.id)}>
                {hospital.name}
              </option>
            ))}
          </AdminSelectField>
        </FilterGroup>

        <FilterGroup>
          <FilterLabel>Wards</FilterLabel>
          {!hospitalId && (
            <FieldHint>Choose a hospital to list its wards.</FieldHint>
          )}
          {hospitalId && wardsLoading && (
            <FieldHint>Loading wards...</FieldHint>
          )}
          {hospitalId &&
            !wardsLoading &&
            wards.map((ward) => (
              <CheckboxField key={ward.id}>
                <input
                  type="checkbox"
                  checked={selectedWardIds.includes(Number(ward.id))}
                  onChange={() => toggleWard(Number(ward.id))}
                />
                {ward.name}
              </CheckboxField>
            ))}
        </FilterGroup>

        {selectedWardIds.length > 1 && (
          <FilterGroup>
            <FilterLabel htmlFor="change-ward-primary">
              Primary ward
            </FilterLabel>
            <AdminSelectField
              id="change-ward-primary"
              value={effectivePrimary === null ? "" : String(effectivePrimary)}
              onChange={(e) => setPrimaryWardId(Number(e.target.value))}
            >
              {selectedWardIds.map((wardId) => {
                const ward = wards.find((item) => Number(item.id) === wardId);
                return (
                  <option key={wardId} value={String(wardId)}>
                    {ward?.name ?? `Ward ${wardId}`}
                  </option>
                );
              })}
            </AdminSelectField>
            <FieldHint>
              The primary ward is the one the patient&apos;s legacy record
              points at, and the one single-ward reports show.
            </FieldHint>
          </FilterGroup>
        )}

        {selectedWardIds.length > 0 && !unchanged && (
          <WarningNotice>
            Moving this patient changes which clinicians can see them, which
            measurement cards their app shows, and which workflows and alerts
            evaluate for them.
            {losingWards.length > 0 &&
              ` They will lose access from ${losingWards
                .map((ward) => ward.wardName)
                .join(", ")}.`}
          </WarningNotice>
        )}

        <ModalFormActions>
          <FilterButton
            variant="secondary"
            disabled={isChangingWards}
            onClick={onDone}
          >
            Cancel
          </FilterButton>
          <FilterButton
            variant="primary"
            disabled={isChangingWards || !canSave}
            onClick={handleSave}
          >
            {isChangingWards ? "Moving..." : "Change wards"}
          </FilterButton>
        </ModalFormActions>
      </ModalForm>
    </ModalContainer>
  );
}
